import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '@hooks/redux'
import {
  fetchDashboards,
  deleteDashboard,
} from '@store/slices/dashboardsSlice'
import { Dashboard } from '@types'

/**
 * Dashboards Page Component
 * 
 * Displays and manages public dashboards for sharing hive data.
 * Features:
 * - List all dashboards with shareable links
 * - Create new dashboards
 * - Edit and delete dashboards
 * - Copy dashboard URLs
 * 
 * @component
 */
const Dashboards: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { dashboards, loadingState, error } = useAppSelector((state) => state.dashboards)

  const [showExplanation, setShowExplanation] = useState(false)

  useEffect(() => {
    dispatch(fetchDashboards())
  }, [dispatch])

  // Note: the public "Open dashboard" share-link view (/dashboard/:code) was removed here
  // because it has no matching route in App.tsx and no public-by-code backend endpoint yet.
  // Reintroduce it properly (route + backend) rather than restoring this button as-is.

  const handleOpen = (dashboard: Dashboard) => {
    navigate(`/dashboards/${dashboard.id}`)
  }

  const handleEdit = (dashboard: Dashboard) => {
    navigate(`/dashboards/${dashboard.id}/edit`)
  }

  const handleDelete = async (dashboard: Dashboard) => {
    if (window.confirm(`Are you sure you want to delete dashboard "${dashboard.name}"?`)) {
      await dispatch(deleteDashboard(dashboard.id))
    }
  }

  const handleCreateNew = () => {
    navigate('/dashboards/create')
  }

  const getHiveCount = (dashboard: Dashboard) => {
    return dashboard.hive_ids?.length || 0
  }

  if (loadingState === 'pending') {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    )
  }

  return (
    <Container sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1">
            Dashboards
          </Typography>
          <Tooltip title="Show explanation">
            <IconButton
              size="small"
              onClick={() => setShowExplanation(!showExplanation)}
              color={showExplanation ? 'primary' : 'default'}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
        >
          New Dashboard
        </Button>
      </Box>

      {/* Explanation */}
      {showExplanation && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setShowExplanation(false)}>
          <Typography variant="body2">
            Dashboards allow you to share real-time hive data publicly via a unique URL.
            Create custom dashboards to showcase specific hives and measurements to others.
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Dashboards Table */}
      {dashboards.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Dashboards
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Create public dashboards to share your hive data with others.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Create First Dashboard
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                {!isMobile && <TableCell align="center">Hives</TableCell>}
                <TableCell>Title</TableCell>
                {!isMobile && <TableCell>Description</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dashboards
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((dashboard) => (
                  <TableRow key={dashboard.id}>
                    <TableCell>
                      <Typography variant="overline">{dashboard.code}</Typography>
                    </TableCell>
                    {!isMobile && (
                      <TableCell align="center">
                        <Chip
                          label={`${getHiveCount(dashboard)} hives`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {dashboard.name}
                      </Typography>
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {dashboard.description || '-'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpen(dashboard)}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(dashboard)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(dashboard)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  )
}

export default Dashboards
